import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';
import {
  BackendErrorResponse,
  StandardHttpError,
  isBackendErrorResponse
} from '../interface/http-error.interface';

/**
 * Servicio centralizado para manejo de errores HTTP
 *
 * @remarks
 * Procesa errores HTTP y muestra modales estandarizados al usuario.
 * Los códigos de error (ER-XXX) son generados por el BACKEND, no localmente.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {

  /**
   * Procesa un error HTTP y lo convierte a StandardHttpError
   *
   * @param error - Error de HttpClient
   * @param interactionId - ID de interacción de la request
   * @returns Error estandarizado
   *
   * @remarks
   * Si el backend retorna una respuesta estructurada con error.code, la usa directamente.
   * Si no, genera un error genérico con código ER-NET-900 (solo para errores de red locales).
   */
  processHttpError(error: HttpErrorResponse, interactionId: string): StandardHttpError {
    const status = error.status;
    const url = error.url || 'unknown';

    // Intentar extraer error estructurado del backend
    if (error.error && isBackendErrorResponse(error.error)) {
      return {
        ...error.error,
        status,
        url,
        originalError: error
      };
    }

    // Si el backend retornó error.error como objeto anidado
    if (error.error?.error && isBackendErrorResponse(error.error.error)) {
      return {
        ...error.error.error,
        status,
        url,
        originalError: error
      };
    }

    // Fallback: error de red/conectividad (frontend no puede contactar backend)
    // Este es el ÚNICO caso donde el frontend genera un código
    return {
      code: 'ER-NET-900',
      message: this.getNetworkErrorMessage(status),
      details: this.getNetworkErrorDetails(status, url),
      timestamp: new Date().toISOString(),
      interactionId,
      status,
      url,
      originalError: error
    };
  }

  /**
   * Muestra el error al usuario mediante modal
   *
   * @param error - Error estandarizado
   */
  async displayError(error: StandardHttpError): Promise<void> {
    const html = this.buildErrorModalHtml(error);

    await Swal.fire({
      icon: 'error',
      title: error.message,
      html,
      confirmButtonText: 'Entendido',
      customClass: {
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false
    });
  }

  /**
   * Construye el HTML del modal de error
   */
  private buildErrorModalHtml(error: StandardHttpError): string {
    let html = '';

    if (error.details) {
      html += `<p class="text-muted mb-3">${error.details}</p>`;
    }

    html += '<div class="mt-3 pt-3 border-top">';
    html += '<p class="mb-0" style="font-size: 0.85rem;">';
    html += `<strong>CE:</strong> <code class="text-danger">${error.code}</code>`;
    html += `<br><span class="text-muted"><strong>Track ID:</strong> ${error.interactionId}</span>`;
    html += '</p>';
    html += '</div>';

    return html;
  }

  /**
   * Obtiene mensaje de error de red según HTTP status
   */
  private getNetworkErrorMessage(status: number): string {
    if (status === 0) {
      return 'Error de conexión';
    }
    if (status === 401) {
      return 'Sesión expirada';
    }
    if (status === 404) {
      return 'Recurso no encontrado';
    }
    if (status >= 500) {
      return 'Error del servidor';
    }
    return 'Error al procesar la solicitud';
  }

  /**
   * Obtiene detalles del error de red
   */
  private getNetworkErrorDetails(status: number, url: string): string {
    if (status === 0) {
      return 'No se pudo establecer conexión con el servidor. Verifica tu conexión a internet.';
    }
    if (status === 401) {
      return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    }
    if (status === 404) {
      return `El endpoint ${url} no existe o no está disponible.`;
    }
    if (status >= 500) {
      return 'El servidor encontró un error inesperado. Por favor, intenta nuevamente más tarde.';
    }
    return 'Ocurrió un error al procesar tu solicitud.';
  }
}
